import { EnumDistributionType } from "@/app/services/generated/enums/enumDistributionType.generated";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import Guid from "@/app/types/common/guid";
import {
  getOneOrManyQueryValuesAsArray,
  parseEnumArrayQuery,
  parseGuidArrayQuery,
} from "@/app/ui/navigation/queryHelpers";
import { MenuItem } from "@/app/ui/navigation/types";
import { EnumDistributionItemType } from "@/components/pages/distribution/types";
import type { Location } from "vue-router";

export const distributionMenu = {
  root: "navigation_distribution_group",
  multiPlanning: {
    root: "multi_planning",
    vehiclePlan: "vehicle_plan",
    allStopsRoute: () => ({
      name: distributionMenu.multiPlanning.root,
    }),
    vehiclePlanRoute: (vehicleId: Guid) => ({
      name: distributionMenu.multiPlanning.vehiclePlan,
      params: {
        vehicleId: vehicleId.toString(),
      },
    }),
  },
  distributionLists: {
    view: "distribution_lists",

    pickingByVehicle: "distribution_picking_list_by_vehicle",
    pickingListByVehicleRoute: (
      despatchDateString: string | null,
      vehicleIds?: Guid[],
      distributionTypes?: EnumDistributionType[]
    ): Location => ({
      name: distributionMenu.distributionLists.pickingByVehicle,
      query: {
        vehicleIds: vehicleIds?.map((id) => id.toString()),
        distributionTypes: distributionTypes?.map((type) =>
          type.key.toString()
        ),
      },
      params: { date: despatchDateString ?? "" },
    }),

    picking: "distribution_picking_list",
    pickingListRoute: (despatchDateString: string | null) => ({
      name: distributionMenu.distributionLists.picking,
      params: { date: despatchDateString ?? "" },
    }),

    loading: "distribution_loading_list",
    loadingListRoute: (despatchDateString: string | null) => ({
      name: distributionMenu.distributionLists.loading,
      params: { date: despatchDateString ?? "" },
    }),

    packing: "distribution_pick_up_list",
    packingListRoute: (despatchDateString: string | null) => ({
      name: distributionMenu.distributionLists.packing,
      params: { date: despatchDateString ?? "" },
    }),
  },
  courierCollection: "courier_collection",
  viewCourierCollection: (): Location => ({
    name: distributionMenu.courierCollection,
  }),
  processing: "distribution_processing",
  viewProcessing: (): Location => ({
    name: distributionMenu.processing,
  }),
  journeyLog: "distribution_journey_log",
  journeyDetailsLog: {
    view: "distribution_journey_details_log",
    viewLocation: (journeyId: Guid): Location => ({
      name: distributionMenu.journeyDetailsLog.view,
      params: {
        journeyId: journeyId.toString(),
      },
    }),
  },
  visitLog: "distribution_visit_log",
  activeJourneys: "distribution_active_journeys",
  pallets: {
    list: "pallet_list",
    listLocation: (): Location => ({
      name: distributionMenu.pallets.list,
    }),
    view: "view_pallet",
    viewLocation: (id: Guid): Location => ({
      name: distributionMenu.pallets.view,
      params: {
        id: id.toString(),
      },
    }),
    edit: "pallet_edit",
    editLocation: (id: Guid): Location => ({
      name: distributionMenu.pallets.edit,
      params: {
        id: id.toString(),
      },
    }),
    create: "pallet_create",
    createLocation: (orderId?: Guid): Location => ({
      name: distributionMenu.pallets.create,
      params: {
        orderId: orderId?.toString() ?? "",
      },
    }),
  },
  settings: {
    root: "navigation_distribution_settings_group",
    fleetManagement: {
      list: "fleet_management",
      listLocation: (): Location => ({
        name: distributionMenu.settings.fleetManagement.list,
      }),
      edit: "fleet_management_edit",
      editLocation: (id: Guid, isEditing: boolean): Location => ({
        name: distributionMenu.settings.fleetManagement.edit,
        params: {
          id: id.toString(),
          mode: isEditing ? "edit" : "view",
        },
      }),
      createLocation: (): Location => ({
        name: distributionMenu.settings.fleetManagement.edit,
      }),
    },
    vehicleChecks: {
      list: "distribution_vehicle_checks",
      listLocation: (): Location => ({
        name: distributionMenu.settings.vehicleChecks.list,
      }),
    },
    schedule: "distribution_schedule",
    create: "vehicle_create",
    edit: "vehicle_edit",
    view: "vehicle_view",
    deliveryServices: {
      list: "distribution_delivery_services",
      listLocation: (): Location => ({
        name: distributionMenu.settings.deliveryServices.list,
      }),
      edit: "distribution_delivery_services_edit",
      editLocation: (id: Guid, isEditing: boolean): Location => ({
        name: distributionMenu.settings.deliveryServices.edit,
        params: {
          id: id.toString(),
          mode: isEditing ? "edit" : "view",
        },
      }),
      createLocation: (): Location => ({
        name: distributionMenu.settings.deliveryServices.edit,
      }),
    },
  },
  orderRecord: "distribution_order_record",
  viewOrderRecord: (id: Guid): Location => ({
    name: distributionMenu.orderRecord,
    params: {
      id: id.toString(),
      distributionItemType: EnumDistributionItemType.order.value,
    },
  }),
  visitRecord: "distribution_visit_record",
  viewVisitRecord: (id: Guid): Location => ({
    name: distributionMenu.visitRecord,
    params: {
      id: id.toString(),
      distributionItemType: EnumDistributionItemType.visit.value,
    },
  }),
  pickupRecord: "distribution_pickup_record",
  viewPickupRecord: (id: Guid): Location => ({
    name: distributionMenu.pickupRecord,
    params: {
      id: id.toString(),
      distributionItemType: EnumDistributionItemType.pickup.value,
    },
  }),
};

export const buildDistributionMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: distributionMenu.root,
    icon: "LocalShipping",
    requiredUserPermissions: [EnumUserPermission.menuDistributionView],
    subMenus: [
      MenuItem.fromOpts({
        name: distributionMenu.multiPlanning.root,
        url: "/distribution/multi-journey-planning/",
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/multijourney/DistributionPlanning.vue"
          ),
        routeMetadata: {
          routeKey: () => "/distribution/multi-journey-planning/",
        },
        childRoutes: [
          {
            name: distributionMenu.multiPlanning.root,
            path: "",
            component: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/multijourney/components/AllStops.vue"
              ),
          },
          {
            name: distributionMenu.multiPlanning.vehiclePlan,
            path: "vehicleplan/:vehicleId",
            component: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/multijourney/components/VehiclePlan.vue"
              ),
            props: (route) => ({
              vehicleId: Guid.tryParse(route.params.vehicleId) || undefined,
            }),
          },
        ],
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.courierCollection,
        url: "/distribution/courier_collection",
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/ListCourierCollection.vue"
          ),
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.distributionLists.view,
        url: "/distribution/lists",
        translationKey: "distribution_picking_lists",
        redirect: "/distribution/lists/picking/vehicle",
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/DistributionPickingLists.vue"
          ),
        componentProps: (route) => ({
          date: route.params.date,
        }),
        routeMetadata: {
          routeKey: () => "/distribution/lists",
        },
        childRoutes: [
          {
            name: distributionMenu.distributionLists.pickingByVehicle,
            path: "picking/vehicle/:date?",
            component: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/subcomponents/PickingListByVehicleTab.vue"
              ),
            props: (route) => ({
              vehicleIds: route.query.vehicleIds
                ? parseGuidArrayQuery(route.query.vehicleIds)
                : [],
              distributionTypes: route.query.distributionTypes
                ? parseEnumArrayQuery(
                    route.query.distributionTypes,
                    EnumDistributionType
                  )
                : [],
            }),
          },
          {
            name: distributionMenu.distributionLists.picking,
            path: "picking/:date?",
            component: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/subcomponents/PickingListByStockLocationTab.vue"
              ),
            props: (route) => ({
              vehicleIds: route.query.vehicleIds
                ? parseGuidArrayQuery(route.query.vehicleIds)
                : [],
              distributionTypes: route.query.distributionTypes
                ? parseEnumArrayQuery(
                    route.query.distributionTypes,
                    EnumDistributionType
                  )
                : [],
            }),
          },
          {
            name: distributionMenu.distributionLists.loading,
            path: "loading/:date?",
            component: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/subcomponents/LoadingListTab.vue"
              ),
            props: (route) => ({
              vehicleIds: route.query.vehicleIds
                ? parseGuidArrayQuery(route.query.vehicleIds)
                : [],
            }),
          },
          {
            name: distributionMenu.distributionLists.packing,
            path: "packing/:date?",
            component: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/subcomponents/PackingListTab.vue"
              ),
            props: (route) => ({
              filterTypes: route.query.filterTypes
                ? getOneOrManyQueryValuesAsArray(route.query.filterTypes)
                : [],
            }),
          },
        ],
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.processing,
        url: "/distribution/processing",
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/DistributionProcessing.vue"
          ),
        requiredUserPermissions: [
          EnumUserPermission.distributionProcessingView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.journeyLog,
        url: "/distribution/journeylog",
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/JourneyLog.vue"
          ),
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.journeyDetailsLog.view,
        url: "/distribution/journeydetailslog/:journeyId",
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/JourneyDetailsLog.vue"
          ),
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
        componentProps: (route) => ({
          journeyId: Guid.tryParse(route.params.journeyId) ?? undefined,
        }),
        visible: false,
      }),
      MenuItem.fromOpts({
        name: distributionMenu.visitLog,
        url: "/distribution/visitLog",
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/VisitLog.vue"
          ),
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.activeJourneys,
        url: "/distribution/activejourneys",
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/ActiveJourneys.vue"
          ),
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.pallets.list,
        url: "/distribution/pallets",
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/pallets/ListPallets.vue"
          ),
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.pallets.edit,
        url: "/distribution/pallets/edit-pallet/:id",
        breadcrumbParentName: distributionMenu.pallets.list,
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/pages/distribution/pallets/EditPalletPage.vue"
          ),
        visible: false,
        componentProps: (route) => {
          return {
            id: route.params.id,
          };
        },
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.pallets.view,
        url: "/distribution/pallets/view-pallet/:id",
        breadcrumbParentName: distributionMenu.pallets.list,
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/pages/distribution/pallets/ViewPalletPage.vue"
          ),
        visible: false,
        componentProps: (route) => {
          return {
            id: route.params.id,
          };
        },
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.pallets.create,
        url: "/distribution/pallets/create",
        breadcrumbParentName: distributionMenu.pallets.list,
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/pages/distribution/pallets/CreatePalletPage.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          orderId: Guid.tryParse(route.params.orderId),
        }),
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),
      MenuItem.forBranch({
        name: distributionMenu.settings.root,
        subMenus: [
          MenuItem.fromOpts({
            name: distributionMenu.settings.fleetManagement.list,
            url: "/distribution/fleetmanagement",
            breadcrumbParentName: distributionMenu.settings.root,
            view: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/ListVehicles.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: distributionMenu.settings.fleetManagement.edit,
            url: "/distribution/fleetmanagement/:id?/:mode?",
            breadcrumbParentName:
              distributionMenu.settings.fleetManagement.list,
            view: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/EditVehicle.vue"
              ),
            visible: false,
          }),
          MenuItem.fromOpts({
            name: distributionMenu.settings.vehicleChecks.list,
            url: "/distribution/vehiclechecks",
            view: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/VehicleCheckList.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: distributionMenu.settings.schedule,
            url: "/distribution/schedule",
            breadcrumbParentName: distributionMenu.settings.root,
            view: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/DistributionSchedule.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: distributionMenu.settings.deliveryServices.list,
            url: "/distribution/deliveryservices",
            breadcrumbParentName: distributionMenu.settings.root,
            view: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/ListDeliveryServices.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: distributionMenu.settings.deliveryServices.edit,
            url: "/distribution/deliveryservice/:id?/:mode?",
            breadcrumbParentName:
              distributionMenu.settings.deliveryServices.list,
            view: () =>
              import(
                /* webpackChunkName: "distribution" */ "@/components/pages/distribution/EditDeliveryService.vue"
              ),
            visible: false,
          }),
        ],
        requiredUserPermissions: [
          EnumUserPermission.distributionManagementView,
        ],
      }),

      MenuItem.fromOpts({
        name: distributionMenu.orderRecord,
        url: "/distribution/orderrecord/:id",
        breadcrumbParentName: distributionMenu.processing,
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/DistributionRecord.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          distributionItemType: EnumDistributionItemType.order,
          id: Guid.tryParse(route.params.id) ?? undefined,
        }),
        requiredUserPermissions: [
          EnumUserPermission.distributionProcessingView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.visitRecord,
        url: "/distribution/visitrecord/:id",
        breadcrumbParentName: distributionMenu.processing,
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/DistributionRecord.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          distributionItemType: EnumDistributionItemType.visit,
          id: Guid.tryParse(route.params.id) ?? undefined,
        }),
        requiredUserPermissions: [
          EnumUserPermission.distributionProcessingView,
        ],
      }),
      MenuItem.fromOpts({
        name: distributionMenu.pickupRecord,
        url: "/distribution/pickuprecord/:id",
        breadcrumbParentName: distributionMenu.processing,
        view: () =>
          import(
            /* webpackChunkName: "distribution" */ "@/components/pages/distribution/DistributionRecord.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          distributionItemType: EnumDistributionItemType.pickup,
          id: Guid.tryParse(route.params.id) ?? undefined,
        }),
        requiredUserPermissions: [
          EnumUserPermission.distributionProcessingView,
        ],
      }),
    ],
  }),
];
